import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  FileText,
  Sparkles,
  Copy,
  Trash2,
  RefreshCw,
  Loader2,
  Plus,
  X,
  Mail,
  Send,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Eye,
  BarChart3,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export default function Perspectives() {
  const [activeTab, setActiveTab] = useState("drafts");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [perspectives, setPerspectives] = useState([]);
  const [draftsThisWeek, setDraftsThisWeek] = useState(0);
  const [maxPerWeek, setMaxPerWeek] = useState(2);
  const [expandedId, setExpandedId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  
  // Recipients state
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");

  // Fetch perspectives
  const fetchPerspectives = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/perspectives`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPerspectives(data.perspectives || []);
        setDraftsThisWeek(data.drafts_this_week || 0);
        setMaxPerWeek(data.max_per_week || 2);
      }
    } catch (error) {
      console.error("Error fetching perspectives:", error);
      toast.error("Failed to load perspectives");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch recipients
  const fetchRecipients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/perspectives/recipients/list`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data || []);
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  }, []);

  useEffect(() => {
    fetchPerspectives();
    fetchRecipients();
  }, [fetchPerspectives, fetchRecipients]);

  // Analyse patterns
  const handleAnalyse = async () => {
    try {
      setAnalysing(true);
      const res = await fetch(`${API}/perspectives/analyse`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
      } else {
        toast.error("Failed to analyse patterns");
      }
    } catch (error) {
      toast.error("Failed to analyse patterns");
    } finally {
      setAnalysing(false);
    }
  };

  // Generate perspective
  const handleGenerate = async (force = false) => {
    if (draftsThisWeek >= maxPerWeek && !force) {
      toast.error(`Maximum ${maxPerWeek} drafts per week reached`);
      return;
    }

    try {
      setGenerating(true);
      const res = await fetch(`${API}/perspectives/generate?force=${force}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success("Perspective generated successfully");
        fetchPerspectives();
        setActiveTab("drafts");
      } else {
        toast.error(data.error || "Failed to generate perspective");
      }
    } catch (error) {
      toast.error("Failed to generate perspective");
    } finally {
      setGenerating(false);
    }
  };

  // Copy to clipboard
  const handleCopy = async (perspective) => {
    try {
      await navigator.clipboard.writeText(perspective.content);
      toast.success("Copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Delete perspective
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this perspective draft?")) return;

    try {
      const res = await fetch(`${API}/perspectives/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Perspective deleted");
        fetchPerspectives();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Send notification
  const handleNotify = async (id) => {
    if (recipients.length === 0) {
      toast.error("Add recipients first");
      return;
    }

    try {
      const res = await fetch(`${API}/perspectives/${id}/notify`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Notification sent to ${data.sent_count} recipient(s)`);
      } else {
        toast.error(data.error || "Failed to send notification");
      }
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  // Add recipient
  const handleAddRecipient = async () => {
    if (!newEmail.trim()) {
      toast.error("Enter an email address");
      return;
    }

    try {
      const res = await fetch(`${API}/perspectives/recipients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newEmail, name: newName }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setNewEmail("");
        setNewName("");
        fetchRecipients();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Failed to add recipient");
    }
  };

  // Remove recipient
  const handleRemoveRecipient = async (email) => {
    try {
      const res = await fetch(`${API}/perspectives/recipients/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchRecipients();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Failed to remove recipient");
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="perspectives-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Perspectives</h1>
            <p className="text-slate-600 mt-1">
              Leadership thought perspectives derived from market and client patterns
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-3 py-1.5">
              <Calendar className="w-3.5 h-3.5 mr-1.5" />
              {draftsThisWeek}/{maxPerWeek} this week
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPerspectives}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="drafts" data-testid="tab-drafts">
            <FileText className="w-4 h-4 mr-2" />
            Drafts
          </TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate">
            <Sparkles className="w-4 h-4 mr-2" />
            Generate
          </TabsTrigger>
          <TabsTrigger value="recipients" data-testid="tab-recipients">
            <Mail className="w-4 h-4 mr-2" />
            Recipients
          </TabsTrigger>
        </TabsList>

        {/* Drafts Tab */}
        <TabsContent value="drafts" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : perspectives.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No perspectives yet</h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  Rex generates perspectives when meaningful patterns are detected across market signals and client relationships.
                </p>
                <Button onClick={() => setActiveTab("generate")}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Check for Patterns
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {perspectives.map((perspective) => (
                <Card
                  key={perspective.id}
                  className={`transition-all ${expandedId === perspective.id ? "ring-2 ring-green-200" : ""}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => setExpandedId(expandedId === perspective.id ? null : perspective.id)}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {perspective.word_count} words
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {formatDate(perspective.generated_at)}
                          </span>
                        </div>
                        <CardTitle className="text-lg leading-tight">
                          {perspective.title}
                        </CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedId(expandedId === perspective.id ? null : perspective.id)}
                      >
                        {expandedId === perspective.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>

                  {expandedId === perspective.id && (
                    <CardContent className="pt-0">
                      {/* Full content */}
                      <div className="bg-slate-50 rounded-lg p-5 mb-4">
                        <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                          {perspective.content}
                        </p>
                      </div>

                      {/* Source patterns */}
                      {perspective.source_patterns?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                            Source Patterns (Internal Reference)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {perspective.source_patterns.map((pattern, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {pattern.pattern} ({pattern.count})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-3 border-t">
                        <Button
                          size="sm"
                          onClick={() => handleCopy(perspective)}
                          data-testid="copy-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy to Clipboard
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleNotify(perspective.id)}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Notify
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                          onClick={() => handleDelete(perspective.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Generate Tab */}
        <TabsContent value="generate" className="space-y-6">
          {/* Pattern Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Pattern Analysis
              </CardTitle>
              <CardDescription>
                Analyse current market signals and relationship patterns to determine if a perspective is warranted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={handleAnalyse}
                disabled={analysing}
                variant="outline"
                className="w-full"
              >
                {analysing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Analyse Patterns
              </Button>

              {analysis && (
                <div className="space-y-4 pt-4 border-t">
                  {/* Summary */}
                  <div className={`p-4 rounded-lg ${analysis.has_interpretive_value ? "bg-green-50 border border-green-200" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {analysis.has_interpretive_value ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="font-medium text-slate-900">
                        {analysis.recommendation}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {analysis.total_patterns} pattern(s) detected
                      {analysis.strongest_pattern && (
                        <> — Strongest: <strong>{analysis.strongest_pattern.pattern}</strong> ({analysis.strongest_pattern.count} instances)</>
                      )}
                    </p>
                  </div>

                  {/* Signal Patterns */}
                  {analysis.signal_patterns?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Market Signal Patterns</p>
                      <div className="space-y-2">
                        {analysis.signal_patterns.map((pattern, i) => (
                          <div key={i} className="p-3 bg-blue-50 rounded-lg text-sm">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">{pattern.pattern}</span>
                              <Badge variant="secondary" className="ml-auto">{pattern.count} signals</Badge>
                            </div>
                            <p className="text-slate-600 mt-1">{pattern.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Relationship Patterns */}
                  {analysis.relationship_patterns?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Relationship Patterns</p>
                      <div className="space-y-2">
                        {analysis.relationship_patterns.map((pattern, i) => (
                          <div key={i} className="p-3 bg-amber-50 rounded-lg text-sm">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <span className="font-medium">{pattern.pattern}</span>
                              <Badge variant="secondary" className="ml-auto">{pattern.count} clients</Badge>
                            </div>
                            <p className="text-slate-600 mt-1">{pattern.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Limit Status */}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4" />
                    {analysis.limit_status}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-green-600" />
                Generate Perspective
              </CardTitle>
              <CardDescription>
                Create a thought leadership draft from detected patterns. Maximum 2 per week.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4">
                <Button
                  onClick={() => handleGenerate(false)}
                  disabled={generating || draftsThisWeek >= maxPerWeek || (analysis && !analysis.has_interpretive_value)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate from Patterns
                </Button>
                <Button
                  onClick={() => handleGenerate(true)}
                  disabled={generating || draftsThisWeek >= maxPerWeek}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Force Generate (Override)
                </Button>
                {draftsThisWeek >= maxPerWeek && (
                  <span className="text-sm text-amber-600">
                    Weekly limit reached ({draftsThisWeek}/{maxPerWeek})
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                <strong>Generate from Patterns:</strong> Rex analyses signals and creates a perspective when meaningful patterns exist.
                <br />
                <strong>Force Generate:</strong> Creates a perspective using general market context even without detected patterns (use sparingly).
              </p>
            </CardContent>
          </Card>

          {/* Guidelines */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Output Guidelines</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-slate-700 mb-2">Structure</p>
                  <ul className="space-y-1 text-slate-600">
                    <li>• Observation (what's happening)</li>
                    <li>• Internal Leadership Experience</li>
                    <li>• Common Misinterpretation</li>
                    <li>• Better Framing</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-slate-700 mb-2">Guardrails</p>
                  <ul className="space-y-1 text-slate-600">
                    <li>• 180-350 words only</li>
                    <li>• No emojis or hashtags</li>
                    <li>• No bullet points</li>
                    <li>• No service mentions</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recipients Tab */}
        <TabsContent value="recipients" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notification Recipients</CardTitle>
              <CardDescription>
                Manage who receives email notifications when new perspectives are generated.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add Recipient */}
              <div className="flex gap-3">
                <Input
                  placeholder="Email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="flex-1"
                  data-testid="recipient-email-input"
                />
                <Input
                  placeholder="Name (optional)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-48"
                />
                <Button onClick={handleAddRecipient} data-testid="add-recipient-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>

              {/* Recipients List */}
              <div className="space-y-2">
                {recipients.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No recipients configured</p>
                    <p className="text-sm">Add recipients to enable notifications</p>
                  </div>
                ) : (
                  recipients.map((recipient, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">
                            {recipient.name || recipient.email}
                          </div>
                          {recipient.name && (
                            <div className="text-sm text-slate-500">{recipient.email}</div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRecipient(recipient.email)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-slate-900">Automated Checks</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Rex automatically checks for patterns on <strong>Tuesday</strong> and <strong>Friday</strong> mornings (10:00 WAT).
                    If meaningful patterns exist and the weekly limit isn't reached, a perspective will be generated and recipients notified.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
